import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';

const SocketContext = createContext();

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const pendingRegistrations = useRef([]);
  
  // Initialiser la connexion Socket.io au montage du composant
  useEffect(() => {
    // Récupérer l'URL du serveur à partir des variables d'environnement ou utiliser localhost par défaut
    const serverUrl = process.env.REACT_APP_API_URL || window.location.origin;
    
    console.log('Tentative de connexion Socket.IO à:', serverUrl);
    
    // Créer l'instance Socket.io
    const socketInstance = io(serverUrl, {
      // Polling D'ABORD, puis upgrade WebSocket : derrière un reverse-proxy (Apache, cloud
      // <slug>.amia.fr), le WS-first se RECONNECTE mal (bloqué sur « Reconnexion… » jusqu'au
      // rechargement). Le long-polling se rétablit de façon fiable puis upgrade vers le WS —
      // le temps réel est conservé, la reconnexion redevient automatique.
      transports: ['polling', 'websocket'],
       upgrade: true,
      reconnection: true,
      // JAMAIS de plafond de reconnexion. Limité à dix tentatives, socket.io
      // abandonnait DÉFINITIVEMENT après ~90 s : le serveur revenait,
      // le client ne le savait plus jamais et affichait « Non connecté » jusqu'à ce qu'un
      // humain recharge la page. Sur un écran de salle d'attente, personne ne recharge —
      // et un redémarrage de serveur, une coupure Wi-Fi ou une mise à jour suffisent.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 30000,
      autoConnect: true,
    });

    // Gestionnaires d'événements
    socketInstance.on('connect', () => {
      console.log('Socket.io connecté avec succès');
      setConnected(true);
      
      // Traiter les enregistrements en attente lorsque la connexion est établie
      if (pendingRegistrations.current.length > 0) {
        console.log(`Traitement de ${pendingRegistrations.current.length} enregistrements en attente`);
        pendingRegistrations.current.forEach(registration => {
          const { clientType, data } = registration;
          socketInstance.emit('register', { clientType, ...data });
          console.log(`Enregistré en attente comme client ${clientType}`);
        });
        
        // Vider la file d'attente
        pendingRegistrations.current = [];
      }
    });

    socketInstance.on('disconnect', (reason) => {
      console.log(`Socket.io déconnecté: ${reason}`);
      setConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('Erreur de connexion Socket.io:', error);
      setConnected(false);
    });

    socketInstance.on('reconnect', (attemptNumber) => {
      console.log(`Socket.io reconnecté après ${attemptNumber} tentatives`);
      setConnected(true);
    });

    socketInstance.on('reconnect_attempt', (attemptNumber) => {
      console.log(`Tentative de reconnexion Socket.io #${attemptNumber}`);
    });

    // Enregistrer l'instance Socket.io dans l'état
    setSocket(socketInstance);

    // Nettoyer la connexion lors du démontage du composant
    return () => {
      if (socketInstance) {
        console.log('Déconnexion du socket lors du démontage du composant');
        socketInstance.disconnect();
      }
    };
  }, []);

  // Fonction pour s'enregistrer en tant que type de client spécifique
  const registerClient = useCallback((clientType, data = {}) => {
    if (socket && connected) {
      // Si le socket est connecté, enregistrer immédiatement
      socket.emit('register', { clientType, ...data });
      console.log(`Enregistré comme client ${clientType}`);
      return true;
    } else {
      // Si le socket n'est pas connecté, mettre l'enregistrement en file d'attente
      console.log(`Socket non connecté, mise en file d'attente de l'enregistrement comme ${clientType}`);
      pendingRegistrations.current.push({ clientType, data });
      return false;
    }
  }, [socket, connected]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        connected,
        registerClient
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
